// Attempt-failure motivation library + contextual selector.
//
// Stage maps to the upcoming attempt: after the 1st miss the student is on
// attempt 2 → 'first' (gentle, analytical guidance); after the 2nd miss they're
// on attempt 3 → 'second' (stronger, persistence-focused). Messages NEVER shame;
// they normalise mistakes, point at a next action, and invite another attempt.

export type MotivationStage = 'first' | 'second';

export interface MotivationCtx {
  /** The upcoming attempt index (2 after the first miss, 3 after the second). */
  attemptIndex: number;
  topicName?: string | null;
  streak?: number;
}

export interface MotivationMessage {
  headline: string;
  body: string;
}

export function stageFor(attemptIndex: number): MotivationStage {
  return attemptIndex >= 3 ? 'second' : 'first';
}

// ── First miss: gentle, guide toward analysis ──
const FIRST: MotivationMessage[] = [
  { headline: 'Good attempt — let’s learn from it', body: 'Re-read the question slowly. What is it actually asking for, and which formula fits?' },
  { headline: 'Almost — now investigate', body: 'Compare your method to the concept. Where did the steps diverge?' },
  { headline: 'Not quite, and that’s useful', body: 'A miss tells you exactly what to review. Check the key idea, then try again.' },
  { headline: 'You’re thinking it through', body: 'Try working backwards from what the answer should look like.' },
  { headline: 'Close — slow down one step', body: 'Mistakes here usually hide in a single step. Re-check each line of your working.' },
  { headline: 'Worth a second look', body: 'Identify the one concept this tests. Refresh it, then attempt again.' },
  { headline: 'Keep going', body: 'Spot the gap between what you did and what the method needs — that’s the lesson.' },
  { headline: 'Analyse, don’t guess', body: 'What’s given, what’s asked, what connects them? Map it, then solve.' },
];

// ── Second miss: stronger, normalise + persist ──
const SECOND: MotivationMessage[] = [
  { headline: 'You’re closer than you think', body: 'Every mistake reveals what to learn next. One more focused attempt.' },
  { headline: 'This is how mastery is built', body: 'Top scorers aren’t the students who never miss — they’re the ones who keep trying.' },
  { headline: 'Turn this into a strength', body: 'This question is an opportunity to fix a weak area before the exam does.' },
  { headline: 'Persistence beats talent here', body: 'Use the hint, rebuild the method, and give it another go — you’ve got this.' },
  { headline: 'Don’t stop now', body: 'The second attempt that follows a miss is where real learning happens.' },
  { headline: 'Struggle means growth', body: 'Your brain is working hardest right now. Lean in and try once more.' },
  { headline: 'Nearly there', body: 'You’ve narrowed it down. Check the hint and take your best shot.' },
  { headline: 'Win the rematch', body: 'Same question, sharper you. Apply what the last attempt taught you.' },
];

let lastFirst = -1;
let lastSecond = -1;

function pickFresh(pool: MotivationMessage[], lastRef: 'first' | 'second'): MotivationMessage {
  const last = lastRef === 'first' ? lastFirst : lastSecond;
  let i = Math.floor(Math.random() * pool.length);
  if (pool.length > 1 && i === last) i = (i + 1) % pool.length;
  if (lastRef === 'first') lastFirst = i; else lastSecond = i;
  return pool[i];
}

/** Pick a contextual message. A topic name (when present) personalises the body. */
export function pickMotivation(ctx: MotivationCtx): MotivationMessage {
  const stage = stageFor(ctx.attemptIndex);
  const base = stage === 'second' ? pickFresh(SECOND, 'second') : pickFresh(FIRST, 'first');
  if (ctx.topicName) {
    return { headline: base.headline, body: `${base.body} (${ctx.topicName})` };
  }
  return base;
}
